import { ArrowBack, Download } from "@mui/icons-material";
import { Alert, Badge, Button, Tab, Tabs, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { CSSProperties, FC, Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import { getDownload } from "../../components/form/info-form-modal/InfoFormModal";
import RiskGrid from "../../components/risk-assessment/RiskGrid";
import RiskSpinner from "../../components/risk-assessment/RiskSpinner";
import { ASSESSMENT_TAB, TABS, leftWidth } from "../../components/risk-assessment/riskColumns";
import { savedDraftRows } from "../../components/risk-assessment/riskDraft";
import {
	EditorRow, RiskGridHandlers, buildLinkIndex, changedTabs, countLabel, emptySelection, isRowChanged
} from "../../components/risk-assessment/riskEditorState";
import { readErrorMessage } from "../../components/risk-assessment/riskErrors";
import { RiskAssessmentService } from "../../services/RiskAssessmentService";
import { RiskHistoryChangesDTO } from "../../types/RiskAssessmentDTO";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from "../execution-events/ExecutionEvents";
import '../risk-assessment-new/RiskAssessmentNew.scss';

const NO_OPTIONS: Record<string, string[]> = {};
const ARCHIVED_HANDLERS: RiskGridHandlers = {
	onToggleRow: () => undefined,
	onToggleLine: () => undefined,
	onStartEdit: () => undefined,
	onCommitEdit: () => undefined,
	onCancelEdit: () => undefined,
	onToggleRowRemoved: () => undefined,
	onAddLine: () => undefined,
	onRemoveLine: () => undefined,
	onRestoreLine: () => undefined
};

const RiskAssessmentChanges: FC = () => {
	const [permissions] = useAtom(permissionsAtom);
	const [changes, setChanges] = useState<RiskHistoryChangesDTO | null>(null);
	const [loading, setLoading] = useState(true);
	const [tab, setTab] = useState(ASSESSMENT_TAB);
	const [downloading, setDownloading] = useState(false);
	const { historyId } = useParams();
	const navigate = useNavigate();
	const { verifyPermission } = useUtils();
	const { showError } = useAlert();
	const service = new RiskAssessmentService();

	useEffect(() => {
		const loadChanges = async () => {
			try {
				const { data } = await service.getHistoryChanges(historyId || '');
				setChanges(data);
			} catch (error) {
				showError(await readErrorMessage(error, 'Não foi possível carregar as alterações desta avaliação.'));
				navigate('/avaliacao-riscos');
			} finally {
				setLoading(false);
			}
		};
		loadChanges();
		if (!!permissions.length) {
			verifyPermission(permissions, 'RISK_ASSESSMENT');
		}
	}, [historyId]);

	const rows = useMemo<EditorRow[]>(() => savedDraftRows(changes?.state || null).filter(isRowChanged), [changes]);
	const linkIndex = useMemo(() => buildLinkIndex(changes?.links || [], changes?.catalog || []), [changes]);
	const tabsWithChanges = useMemo(() => changedTabs(rows), [rows]);
	const history = changes?.history;

	return (
		<Fragment>
			<AppBar
				modulo="Avaliação de riscos"
			/>
			<div className="risk-assessment-new">
				{loading || !history
					? getLoading()
					: <Fragment>
						<div className="cabecalho-fluxo">
							<div className="titulo">
								<Typography variant="h6">Alterações da avaliação — somente as linhas alteradas</Typography>
								<span>
									{history.companyName} - {history.companyId} · Data: {history.assessmentDate}
									{' · '}Validade: {history.validityDate}
								</span>
								<span>Finalizada em {history.updatedAt} por {history.responsibleName || history.name}</span>
							</div>
							<div className="contadores">
								<span>{countLabel(rows.length, 'linha alterada', 'linhas alteradas')}</span>
							</div>
							<Button variant="outlined" startIcon={<ArrowBack/>} onClick={() => navigate('/avaliacao-riscos')}>
								Voltar ao histórico
							</Button>
							<Button
								variant="outlined"
								startIcon={downloading ? <RiskSpinner size={18}/> : <Download/>}
								disabled={downloading}
								onClick={download}
							>
								Baixar planilha gerada
							</Button>
						</div>
						{!rows.length && (
							<Alert severity="info">
								Nenhuma linha alterada; a avaliação saiu com as mesmas configurações e as novas datas.
							</Alert>
						)}
						<div className="abas" style={{ '--inicio-direita': `${leftWidth() + 2}px` } as CSSProperties}>
							<div className="acoes-grade"/>
							<Tabs value={tab} onChange={(_, next) => setTab(next)} variant="scrollable" scrollButtons="auto">
								{TABS.map(name => (
									<Tab
										key={name}
										value={name}
										label={<Badge color="warning" variant="dot" invisible={!tabsWithChanges.has(name)}>{name}</Badge>}
									/>
								))}
							</Tabs>
						</div>
						<RiskGrid
							rows={rows}
							tab={tab}
							readOnly
							filters={{}}
							filterOptions={NO_OPTIONS}
							onFilterChange={() => undefined}
							selection={emptySelection()}
							headerCheck="unchecked"
							onToggleAll={() => undefined}
							bulk={{}}
							onBulkChange={() => undefined}
							onApplyBulk={() => undefined}
							onClearBulk={() => undefined}
							busy=""
							bulkRisks={[]}
							canApplyBulk={false}
							editing={null}
							handlers={ARCHIVED_HANDLERS}
							linkIndex={linkIndex}
							options={NO_OPTIONS}
						/>
					</Fragment>
				}
			</div>
		</Fragment>
	);

	async function download() {
		if (!history) {
			return;
		}
		setDownloading(true);
		const response = await service.downloadHistoryFile(history.id);
		setDownloading(false);
		if (!response) {
			showError('Não foi possível baixar a planilha.');
			return;
		}
		getDownload(response, history.fileName.replace(/\.xlsx$/i, ''));
	}
};

export default RiskAssessmentChanges;